import { pickClass, getActiveClass } from "@/lib/activeClass";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Plus, Trash2, Upload, Download, Save, Search, Edit3, FileText,
  CheckCircle2, AlertTriangle, XCircle, Users as UsersIcon, X, UserMinus,
  Eye, Printer, ChevronRight, MoreVertical,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const sortAlpha = (a, b) =>
  (a.nom || "").localeCompare(b.nom || "", "fr", { sensitivity: "base" }) ||
  (a.prenom || "").localeCompare(b.prenom || "", "fr", { sensitivity: "base" });

export default function Evaluations() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "config";
  const inConfig = activeTab === "config";
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [newNom, setNewNom] = useState("Composition 1er trimestre");

  const currentClass = async () => {
    if (user.role !== "maitre") return undefined;
    const saved = getActiveClass();
    if (saved) return saved;
    const { data } = await api.get("/classes");
    return pickClass(data) || undefined;
  };

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/evaluations/sessions", { params: { class_id: await currentClass() } });
      setSessions(data);
      if (data.length > 0 && !sessionId) setSessionId(data[0].id);
      if (data.length === 0) setSessionId("");
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { loadSessions(); /* eslint-disable-next-line */ }, []);

  const createSession = async () => {
    if (!newNom.trim()) return;
    try {
      const { data } = await api.post("/evaluations/sessions", { nom: newNom.trim(), class_id: await currentClass() });
      toast.success("Évaluation créée");
      setNewOpen(false);
      await loadSessions();
      setSessionId(data.id);
    } catch (e) { toast.error(formatError(e)); }
  };

  const deleteSession = async () => {
    if (!sessionId) return;
    if (!confirm("Supprimer cette évaluation et toutes ses données ?")) return;
    try {
      await api.delete(`/evaluations/sessions/${sessionId}`);
      toast.success("Supprimée");
      setSessionId("");
      loadSessions();
    } catch (e) { toast.error(formatError(e)); }
  };

  const canEdit = user.role === "maitre";

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Évaluations</h1>
        <p className="text-sm text-[#5C736A]">Compositions, essais et autres évaluations de la classe</p>
      </div>

      <div className="ek-card p-3 flex flex-col sm:flex-row sm:items-center gap-2">
        <Select value={sessionId} onValueChange={setSessionId}>
          <SelectTrigger className="flex-1" data-testid="eval-session-select">
            <SelectValue placeholder={sessions.length ? "Choisir une évaluation" : "Aucune évaluation"} />
          </SelectTrigger>
          <SelectContent>
            {sessions.map((s) => <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>)}
          </SelectContent>
        </Select>
        {canEdit && inConfig && (
          <div className="flex gap-2">
            <Dialog open={newOpen} onOpenChange={setNewOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="flex-1 sm:flex-none" data-testid="eval-new-session">
                  <Plus className="w-4 h-4 mr-1" /> Nouvelle
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nouvelle évaluation</DialogTitle></DialogHeader>
                <div className="space-y-2">
                  <Label>Nom (ex : Composition 1er trimestre, Essai n°1...)</Label>
                  <Input value={newNom} onChange={(e) => setNewNom(e.target.value)} />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setNewOpen(false)}>Annuler</Button>
                  <Button onClick={createSession} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="eval-submit-session">Créer</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {sessionId && (
              <Button variant="destructive" onClick={deleteSession} className="flex-1 sm:flex-none" data-testid="eval-delete-session">
                <Trash2 className="w-4 h-4 mr-1" /> Suppr.
              </Button>
            )}
          </div>
        )}
      </div>

      {!sessionId && (
        <div className="ek-card p-10 text-center text-sm text-[#5C736A]">
          Aucune évaluation. {canEdit && "Cliquez sur « Nouvelle » pour démarrer."}
        </div>
      )}

      {sessionId && <SessionTabs key={sessionId} sessionId={sessionId} canEdit={canEdit} canGrade={user.role === "maitre"} />}
    </div>
  );
}

/* -------------------- SESSION TABS -------------------- */
const VALID_TABS = ["config", "students", "notes", "results", "bulletins"];

function SessionTabs({ sessionId, canEdit, canGrade }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const initialTab = VALID_TABS.includes(urlTab) ? urlTab : "config";
  const [tab, setTab] = useState(initialTab);

  // Sync URL -> state when user clicks a sidebar sub-menu link
  useEffect(() => {
    if (urlTab && VALID_TABS.includes(urlTab) && urlTab !== tab) {
      setTab(urlTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTab]);

  // Sync state -> URL when user clicks a tab (keep other params intact)
  const handleTabChange = (v) => {
    setTab(v);
    const next = new URLSearchParams(searchParams);
    next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  return (
    <Tabs value={tab} onValueChange={handleTabChange}>
      <TabsList className="bg-white border border-[#E2E8F0] w-full flex overflow-x-auto h-auto p-1">
        <TabsTrigger value="config" data-testid="tab-config" className="flex-1 text-xs sm:text-sm">Configuration matières</TabsTrigger>
        <TabsTrigger value="students" data-testid="tab-students" className="flex-1 text-xs sm:text-sm">Candidats</TabsTrigger>
        <TabsTrigger value="notes" data-testid="tab-notes" className="flex-1 text-xs sm:text-sm">Notes</TabsTrigger>
        <TabsTrigger value="results" data-testid="tab-results" className="flex-1 text-xs sm:text-sm">Résultats</TabsTrigger>
        <TabsTrigger value="bulletins" data-testid="tab-bulletins" className="flex-1 text-xs sm:text-sm">Bulletin</TabsTrigger>
      </TabsList>
      <TabsContent value="config" className="mt-5"><ConfigTab sessionId={sessionId} canEdit={canEdit} /></TabsContent>
      <TabsContent value="students" className="mt-5"><StudentsTab sessionId={sessionId} canEdit={canEdit} /></TabsContent>
      <TabsContent value="notes" className="mt-5"><NotesTab sessionId={sessionId} canEdit={canGrade} /></TabsContent>
      <TabsContent value="results" className="mt-5"><ResultsTab sessionId={sessionId} /></TabsContent>
      <TabsContent value="bulletins" className="mt-5"><BulletinsTab sessionId={sessionId} /></TabsContent>
    </Tabs>
  );
}

/* -------------------- CONFIG -------------------- */
function ConfigTab({ sessionId, canEdit }) {
  const [session, setSession] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [candCount, setCandCount] = useState(0);
  const [subjOpen, setSubjOpen] = useState(false);
  const [editingSubj, setEditingSubj] = useState(null);

  const load = async () => {
    try {
      const [sess, subs, cands] = await Promise.all([
        api.get("/evaluations/sessions").then(({ data }) => data.find((s) => s.id === sessionId)),
        api.get("/evaluations/subjects", { params: { session_id: sessionId } }),
        api.get("/evaluations/candidates", { params: { session_id: sessionId } }),
      ]);
      setSession(sess);
      setSubjects(subs.data);
      setCandCount(cands.data.length);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sessionId]);

  const totalBareme = subjects.reduce((s, x) => s + (x.points || 0), 0);

  const saveSession = async (patch) => {
    try {
      const { data } = await api.patch(`/evaluations/sessions/${sessionId}`, patch);
      setSession(data);
      toast.success("Enregistré");
    } catch (e) { toast.error(formatError(e)); }
  };

  const openAddSubj = () => { setEditingSubj(null); setSubjOpen(true); };
  const openEditSubj = (s) => { setEditingSubj(s); setSubjOpen(true); };
  const submitSubj = async (data) => {
    try {
      if (editingSubj) await api.patch(`/evaluations/subjects/${editingSubj.id}`, data);
      else await api.post(`/evaluations/subjects`, { ...data, session_id: sessionId });
      toast.success("Matière enregistrée");
      setSubjOpen(false);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const delSubj = async (s) => {
    if (!confirm(`Supprimer ${s.nom} ? (notes perdues)`)) return;
    try { await api.delete(`/evaluations/subjects/${s.id}`); load(); } catch (e) { toast.error(formatError(e)); }
  };

  if (!session) return <div className="skel h-32" />;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Matières" value={subjects.length} />
        <StatCard label="Élèves" value={candCount} />
        <StatCard label="Total barème" value={totalBareme} />
      </div>

      <div className="ek-card p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Matières & barème</h3>
          {canEdit && (
            <Button size="sm" onClick={openAddSubj} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="add-eval-subj">
              <Plus className="w-4 h-4 mr-1" /> Matière
            </Button>
          )}
        </div>
        <div className="space-y-2">
          {subjects.map((s) => (
            <div key={s.id} className="p-3 rounded-lg bg-slate-50">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-semibold">{s.nom}</div>
                  <div className="text-xs text-[#5C736A]">
                    {s.has_sub_domains
                      ? `${(s.sub_domains || []).length} sous-domaine(s) · Total /${s.points}`
                      : `Total /${s.points}`}
                  </div>
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEditSubj(s)}><Edit3 className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => delSubj(s)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  </div>
                )}
              </div>
              {s.has_sub_domains && (
                <div className="mt-2 pl-3 border-l-2 border-[#E6B800] space-y-1">
                  {(s.sub_domains || []).map((sd, i) => (
                    <div key={i} className="text-xs text-[#5C736A]">• {sd.nom} <span className="font-semibold text-[#1A2E28]">/{sd.points}</span></div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {subjects.length === 0 && <div className="text-sm text-[#5C736A] text-center py-4">Aucune matière. Ajoutez-en une.</div>}
        </div>
      </div>

      <SubjectDialog open={subjOpen} onOpenChange={setSubjOpen} initial={editingSubj} onSubmit={submitSubj} />
      <HeaderForm session={session} onSave={saveSession} disabled={!canEdit} />
      <ThresholdsForm session={session} onSave={saveSession} disabled={!canEdit} totalBareme={totalBareme} />
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="ek-card p-3 sm:p-5 text-center">
      <div className="text-xs sm:text-sm text-[#5C736A]">{label}</div>
      <div className="text-2xl sm:text-4xl font-bold text-[#1F7A63] mt-1" style={{ fontFamily: "Outfit" }}>{value}</div>
    </div>
  );
}

function SubjectDialog({ open, onOpenChange, initial, onSubmit }) {
  const [f, setF] = useState({ nom: "", points: 0, has_sub_domains: false, sub_domains: [] });
  useEffect(() => {
    if (open) {
      setF(initial
        ? { nom: initial.nom, points: initial.points || 0, has_sub_domains: !!initial.has_sub_domains, sub_domains: initial.sub_domains || [] }
        : { nom: "", points: 0, has_sub_domains: false, sub_domains: [] });
    }
  }, [open, initial]);

  const totalSub = f.sub_domains.reduce((s, x) => s + (Number(x.points) || 0), 0);
  const addSD = () => setF({ ...f, sub_domains: [...f.sub_domains, { nom: "", points: 0 }] });
  const updSD = (i, k, v) => {
    const arr = [...f.sub_domains];
    arr[i] = { ...arr[i], [k]: k === "points" ? Number(v) : v };
    setF({ ...f, sub_domains: arr });
  };
  const rmSD = (i) => setF({ ...f, sub_domains: f.sub_domains.filter((_, idx) => idx !== i) });

  const submit = () => {
    if (!f.nom.trim()) { toast.error("Nom requis"); return; }
    if (!f.has_sub_domains && (!f.points || f.points <= 0)) { toast.error("Points requis"); return; }
    if (f.has_sub_domains && f.sub_domains.length === 0) { toast.error("Ajoutez au moins un sous-domaine"); return; }
    onSubmit({
      nom: f.nom.trim(),
      points: f.has_sub_domains ? totalSub : Number(f.points),
      has_sub_domains: f.has_sub_domains,
      sub_domains: f.has_sub_domains ? f.sub_domains.filter((s) => s.nom.trim()) : [],
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial ? "Modifier la matière" : "Nouvelle matière"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nom du domaine</Label>
            <Input data-testid="subj-nom" value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} placeholder="ex : Langue et communication" />
          </div>
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <div className="font-semibold text-sm">Activer les sous-domaines</div>
              <div className="text-xs text-[#5C736A]">Découpez la matière en plusieurs sous-domaines.</div>
            </div>
            <Switch checked={f.has_sub_domains} onCheckedChange={(v) => setF({ ...f, has_sub_domains: v })} data-testid="subj-toggle-sd" />
          </div>
          {!f.has_sub_domains && (
            <div>
              <Label>Nombre de points</Label>
              <Input type="number" min="0" step="0.5" data-testid="subj-points" value={f.points} onChange={(e) => setF({ ...f, points: Number(e.target.value) })} />
            </div>
          )}
          {f.has_sub_domains && (
            <div className="space-y-2">
              <Label>Sous-domaines</Label>
              {f.sub_domains.map((sd, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input placeholder="Nom" value={sd.nom} onChange={(e) => updSD(i, "nom", e.target.value)} />
                  <Input type="number" step="0.5" placeholder="Pts" value={sd.points} onChange={(e) => updSD(i, "points", e.target.value)} className="w-24" />
                  <Button variant="ghost" size="icon" onClick={() => rmSD(i)}><X className="w-4 h-4" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addSD}><Plus className="w-3 h-3 mr-1" /> Sous-domaine</Button>
              <div className="text-sm text-right">Total : <strong className="text-[#1F7A63]">{totalSub}</strong> pts</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HeaderForm({ session, onSave, disabled }) {
  const [f, setF] = useState({
    ia: session.ia || "", ief: session.ief || "",
    annee_scolaire: session.annee_scolaire || "2025-2026",
    centre: session.centre || "", signataire: session.signataire || "",
  });
  return (
    <div className="ek-card p-4 sm:p-5">
      <h3 className="font-semibold mb-3" style={{ fontFamily: "Outfit" }}>En-tête & signature</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label>IA</Label><Input disabled={disabled} value={f.ia} onChange={(e) => setF({ ...f, ia: e.target.value })} /></div>
        <div><Label>IEF</Label><Input disabled={disabled} value={f.ief} onChange={(e) => setF({ ...f, ief: e.target.value })} /></div>
        <div><Label>Année scolaire</Label><Input disabled={disabled} value={f.annee_scolaire} onChange={(e) => setF({ ...f, annee_scolaire: e.target.value })} /></div>
        <div><Label>Nom du centre</Label><Input disabled={disabled} value={f.centre} onChange={(e) => setF({ ...f, centre: e.target.value })} /></div>
        <div className="md:col-span-2"><Label>Signataire</Label><Input disabled={disabled} value={f.signataire} onChange={(e) => setF({ ...f, signataire: e.target.value })} /></div>
      </div>
      {!disabled && (
        <Button onClick={() => onSave(f)} className="mt-4 bg-[#1F7A63] hover:bg-[#165c4b] text-white w-full sm:w-auto">
          <Save className="w-4 h-4 mr-1" /> Enregistrer
        </Button>
      )}
    </div>
  );
}

function ThresholdsForm({ session, onSave, disabled, totalBareme }) {
  const [f, setF] = useState({
    admission_min: session.admission_min ?? 0,
    repechage_min: session.repechage_min ?? 0,
  });
  return (
    <div className="ek-card p-4 sm:p-5">
      <h3 className="font-semibold mb-1" style={{ fontFamily: "Outfit" }}>Seuils (en points)</h3>
      <p className="text-xs text-[#5C736A] mb-3">Saisis en points absolus. Total barème = {totalBareme}.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Admission ≥ (points)</Label>
          <Input type="number" min="0" step="0.5" disabled={disabled}
            value={f.admission_min} onChange={(e) => setF({ ...f, admission_min: Number(e.target.value) })} />
        </div>
        <div>
          <Label>Repêchage ≥ (points)</Label>
          <Input type="number" min="0" step="0.5" disabled={disabled}
            value={f.repechage_min} onChange={(e) => setF({ ...f, repechage_min: Number(e.target.value) })} />
        </div>
      </div>
      {!disabled && (
        <Button onClick={() => onSave(f)} className="mt-4 bg-[#1F7A63] hover:bg-[#165c4b] text-white w-full sm:w-auto">
          <Save className="w-4 h-4 mr-1" /> Enregistrer
        </Button>
      )}
    </div>
  );
}

/* -------------------- ÉLÈVES -------------------- */
function StudentsTab({ sessionId, canEdit }) {
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ prenom: "", nom: "", numero_table: "" });
  const [importOpen, setImportOpen] = useState(false);
  const [importClassId, setImportClassId] = useState("");

  const load = async () => {
    try {
      const [c, cls] = await Promise.all([
        api.get("/evaluations/candidates", { params: { session_id: sessionId } }),
        api.get("/classes"),
      ]);
      setItems([...c.data].sort(sortAlpha));
      setClasses(cls.data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sessionId]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter((c) => `${c.prenom} ${c.nom}`.toLowerCase().includes(s));
  }, [items, search]);

  const submit = async () => {
    try {
      const body = { ...form, numero_table: form.numero_table ? Number(form.numero_table) : null };
      if (editing) await api.patch(`/evaluations/candidates/${editing.id}`, body);
      else await api.post(`/evaluations/candidates`, { ...body, session_id: sessionId });
      toast.success("Élève enregistré");
      setOpen(false);
      setForm({ prenom: "", nom: "", numero_table: "" });
      setEditing(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const del = async (c) => {
    if (!confirm(`Supprimer ${c.prenom} ${c.nom} ?`)) return;
    try { await api.delete(`/evaluations/candidates/${c.id}`); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const toggleAbsent = async (c) => {
    try {
      await api.patch(`/evaluations/candidates/${c.id}`, { absent: !c.absent });
      load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const openEdit = (c) => { setEditing(c); setForm({ prenom: c.prenom, nom: c.nom, numero_table: c.numero_table || "" }); setOpen(true); };
  const openAdd = () => { setEditing(null); setForm({ prenom: "", nom: "", numero_table: "" }); setOpen(true); };

  const doImportClass = async () => {
    if (!importClassId) { toast.error("Choisissez une classe"); return; }
    try {
      const { data } = await api.post(`/evaluations/sessions/${sessionId}/import-class?class_id=${importClassId}`);
      toast.success(`${data.created} élève(s) importé(s)`);
      setImportOpen(false);
      setImportClassId("");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="ek-card p-3 space-y-2 sm:space-y-0 sm:flex sm:items-center sm:gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C736A]" />
          <Input className="pl-9" placeholder="Rechercher un élève…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {canEdit && (
          <div className="grid grid-cols-2 sm:flex gap-2">
            <Dialog open={importOpen} onOpenChange={setImportOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="import-from-class"><Upload className="w-4 h-4 mr-1" /> Importer classe</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Importer les élèves d&apos;une classe</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <p className="text-sm text-[#5C736A]">Tous les élèves de la classe seront ajoutés (sans doublon).</p>
                  <Select value={importClassId} onValueChange={setImportClassId}>
                    <SelectTrigger><SelectValue placeholder="Choisir une classe" /></SelectTrigger>
                    <SelectContent>
                      {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom} ({c.niveau})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setImportOpen(false)}>Annuler</Button>
                  <Button onClick={doImportClass} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white">Importer</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button onClick={openAdd} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </div>
        )}
      </div>

      <div className="ek-card p-0" data-testid="candidates-table">
        <div className="px-3 py-2 border-b border-[#E2E8F0] flex items-center gap-4 text-xs text-[#5C736A]">
          <span>{filtered.length} candidat(s)</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#1F7A63]" /> Présent</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Absent</span>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">N°</TableHead>
              <TableHead className="w-10 text-center" title="Présence">●</TableHead>
              <TableHead>Élève</TableHead>
              <TableHead className="hidden md:table-cell">Matricule</TableHead>
              <TableHead className="hidden md:table-cell">Sexe</TableHead>
              <TableHead className="hidden lg:table-cell">Naissance</TableHead>
              <TableHead className="hidden lg:table-cell">Classe</TableHead>
              <TableHead className="hidden xl:table-cell">Tuteur</TableHead>
              <TableHead className="w-12 text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <CandidateRow key={c.id} c={c} canEdit={canEdit}
                onToggle={() => toggleAbsent(c)} onEdit={() => openEdit(c)} onDelete={() => del(c)} />
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-sm text-[#5C736A] py-8">Aucun élève. Importez-en depuis une classe.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Modifier l'élève" : "Nouvel élève"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Prénom</Label><Input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} /></div>
            <div><Label>Nom</Label><Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} /></div>
            <div><Label>N° (optionnel)</Label><Input type="number" value={form.numero_table} onChange={(e) => setForm({ ...form, numero_table: e.target.value })} placeholder="Auto si vide" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const sexeLabel = (x) => (x === "F" ? "Féminin" : x === "M" ? "Masculin" : "—");

function CandidateRow({ c, canEdit, onToggle, onEdit, onDelete }) {
  const st = c.student || {};
  const naissance = [st.date_naissance, st.lieu_naissance].filter(Boolean).join(" à ") || "—";
  return (
    <TableRow className={c.absent ? "bg-red-50/50" : ""} data-testid={`candidate-row-${c.numero_table}`}>
      <TableCell className="font-bold">{c.numero_table}</TableCell>
      <TableCell className="text-center">
        <button
          type="button" disabled={!canEdit} onClick={onToggle}
          title={c.absent ? "Absent — cliquer pour marquer présent" : "Présent — cliquer pour marquer absent"}
          className={`w-3.5 h-3.5 rounded-full inline-block transition-transform hover:scale-125 ${c.absent ? "bg-red-500" : "bg-[#1F7A63]"}`}
          data-testid={`presence-dot-${c.numero_table}`}
        />
      </TableCell>
      <TableCell>
        <div className="font-semibold"><span className="font-normal">{c.prenom}</span> {c.nom}</div>
        <div className="md:hidden text-[11px] text-[#5C736A]">{[st.matricule, st.sexe && sexeLabel(st.sexe)].filter(Boolean).join(" · ")}</div>
      </TableCell>
      <TableCell className="hidden md:table-cell font-mono text-xs">{st.matricule || "—"}</TableCell>
      <TableCell className="hidden md:table-cell">{sexeLabel(st.sexe)}</TableCell>
      <TableCell className="hidden lg:table-cell text-sm">{naissance}</TableCell>
      <TableCell className="hidden lg:table-cell">{st.classe || "—"}</TableCell>
      <TableCell className="hidden xl:table-cell text-sm">{st.tuteur || "—"}</TableCell>
      <TableCell className="text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" data-testid={`candidate-menu-${c.numero_table}`}><MoreVertical className="w-4 h-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="text-xs">{c.prenom} {c.nom}</DropdownMenuLabel>
            <div className="px-2 pb-2 text-xs space-y-1 text-[#5C736A]" data-testid={`candidate-details-${c.numero_table}`}>
              <div>Matricule : <b className="text-[#1A2E28]">{st.matricule || "—"}</b></div>
              <div>Sexe : <b className="text-[#1A2E28]">{sexeLabel(st.sexe)}</b></div>
              <div>Naissance : <b className="text-[#1A2E28]">{naissance}</b></div>
              <div>Classe : <b className="text-[#1A2E28]">{st.classe || "—"}</b></div>
              <div>Tuteur : <b className="text-[#1A2E28]">{st.tuteur || "—"}</b></div>
              <div>Téléphone : <b className="text-[#1A2E28]">{st.telephone_parent || "—"}</b></div>
              <div>Statut : <b className={c.absent ? "text-red-600" : "text-[#1F7A63]"}>{c.absent ? "Absent" : "Présent"}</b></div>
            </div>
            {canEdit && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onToggle}><UserMinus className="w-4 h-4 mr-2" />{c.absent ? "Marquer présent" : "Marquer absent"}</DropdownMenuItem>
                <DropdownMenuItem onClick={onEdit}><Edit3 className="w-4 h-4 mr-2" />Modifier</DropdownMenuItem>
                <DropdownMenuItem onClick={onDelete} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Supprimer</DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

/* -------------------- NOTES (par domaine, alphabétique) -------------------- */
function NotesTab({ sessionId, canEdit }) {
  const [subjects, setSubjects] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [grades, setGrades] = useState({}); // key = candidate_id|subject_id|sub_domain
  const [domainKey, setDomainKey] = useState("");
  const [draft, setDraft] = useState({});
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, c, g] = await Promise.all([
        api.get("/evaluations/subjects", { params: { session_id: sessionId } }),
        api.get("/evaluations/candidates", { params: { session_id: sessionId } }),
        api.get("/evaluations/grades", { params: { session_id: sessionId } }),
      ]);
      setSubjects(s.data);
      setCandidates([...c.data].sort(sortAlpha));
      const map = {};
      g.data.forEach((x) => {
        map[`${x.candidate_id}|${x.subject_id}|${x.sub_domain || ""}`] = x.valeur;
      });
      setGrades(map);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sessionId]);

  // Build domain options : one entry per subject with sub-domains + one "simples" entry grouping all simple subjects
  const domains = useMemo(() => {
    const simples = subjects.filter((s) => !s.has_sub_domains);
    const withSub = subjects.filter((s) => s.has_sub_domains);
    const list = [];
    if (simples.length > 0) list.push({ id: "simples", label: `Matières simples (${simples.length})`, subjects: simples });
    withSub.forEach((s) => list.push({ id: s.id, label: s.nom, subjects: [s] }));
    return list;
  }, [subjects]);

  // Auto-pick the first domain when subjects load
  useEffect(() => {
    if (!domainKey && domains.length > 0) setDomainKey(domains[0].id);
  }, [domains, domainKey]);

  const activeDomain = domains.find((d) => d.id === domainKey);

  // Initialise draft when active domain changes (load existing values)
  useEffect(() => {
    if (!activeDomain) { setDraft({}); setDirty({}); return; }
    const d = {};
    candidates.forEach((c) => {
      activeDomain.subjects.forEach((s) => {
        if (s.has_sub_domains) {
          (s.sub_domains || []).forEach((sd) => {
            d[`${c.id}|${s.id}|${sd.nom}`] = grades[`${c.id}|${s.id}|${sd.nom}`] ?? "";
          });
        } else {
          d[`${c.id}|${s.id}|`] = grades[`${c.id}|${s.id}|`] ?? "";
        }
      });
    });
    setDraft(d);
    setDirty({});
  }, [domainKey, candidates, grades, activeDomain]);

  const setVal = (k, v) => { setDraft({ ...draft, [k]: v }); setDirty({ ...dirty, [k]: true }); };

  const saveDomain = async () => {
    setSaving(true);
    let ok = 0, fail = 0;
    for (const k of Object.keys(dirty)) {
      const raw = draft[k];
      if (raw === "" || raw == null) continue;
      const v = parseFloat(String(raw).replace(",", "."));
      if (isNaN(v)) { fail++; continue; }
      const [cid, sid, sd] = k.split("|");
      try {
        await api.put("/evaluations/grades", {
          candidate_id: cid, subject_id: sid, sub_domain: sd || null, valeur: v,
        });
        ok++;
      } catch (e) { fail++; toast.error(formatError(e)); }
    }
    setSaving(false);
    if (ok) toast.success(`${ok} note(s) enregistrée(s)`);
    if (fail) toast.error(`${fail} échec(s)`);
    load();
  };

  if (subjects.length === 0) {
    return <div className="ek-card p-10 text-center text-sm text-[#5C736A]">Configurez d&apos;abord les matières.</div>;
  }
  if (candidates.length === 0) {
    return <div className="ek-card p-10 text-center text-sm text-[#5C736A]">Ajoutez d&apos;abord des élèves.</div>;
  }

  // Build columns for the active domain
  const columns = [];
  (activeDomain?.subjects || []).forEach((s) => {
    if (s.has_sub_domains) {
      (s.sub_domains || []).forEach((sd) => {
        columns.push({ subject: s, sub: sd.nom, points: sd.points, label: sd.nom });
      });
    } else {
      columns.push({ subject: s, sub: null, points: s.points, label: s.nom });
    }
  });

  const dirtyCount = Object.keys(dirty).length;

  return (
    <div className="space-y-4">
      {/* Domain stepper */}
      <div className="ek-card p-3 space-y-2 sm:space-y-0 sm:flex sm:items-center sm:gap-2">
        <Label className="text-xs text-[#5C736A] uppercase tracking-wider shrink-0 mb-1 sm:mb-0">Domaine</Label>
        <Select value={domainKey} onValueChange={setDomainKey}>
          <SelectTrigger className="flex-1" data-testid="notes-domain-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {domains.map((d) => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {canEdit && (
          <Button
            onClick={saveDomain}
            disabled={dirtyCount === 0 || saving}
            className="bg-[#1F7A63] hover:bg-[#165c4b] text-white w-full sm:w-auto"
            data-testid="notes-save"
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? "Enregistrement…" : dirtyCount > 0 ? `Enregistrer (${dirtyCount})` : "Enregistrer"}
          </Button>
        )}
      </div>

      {/* Description */}
      <div className="ek-card p-3 sm:p-4 bg-[#FCF8E6]/40 border border-[#E6B800]/40">
        <p className="text-sm text-[#1A2E28]">
          <strong>{activeDomain?.label}</strong> — Saisissez les notes pour <strong>tous les élèves</strong> sur ce domaine, puis cliquez sur « Enregistrer » avant de passer au domaine suivant.
        </p>
      </div>

      {/* Notes grid (mobile-first) */}
      <div className="ek-card p-0 overflow-hidden">
        <div className="ek-table-wrap">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-[#E6B800]">
              <tr>
                <th className="px-2 py-2 sm:sticky sm:left-0 bg-[#E6B800] z-20 text-center w-10 sm:w-12">N°</th>
                <th className="px-2 py-2 sm:sticky sm:left-10 bg-[#E6B800] z-20 text-left min-w-[80px] sm:min-w-[140px]">ÉLÈVE</th>
                {columns.map((col, i) => (
                  <th key={i} className="px-2 py-2 text-center whitespace-nowrap text-xs">
                    <div className="font-bold">{col.label}</div>
                    <div className="text-[10px] font-normal">/{col.points}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {candidates.map((c, idx) => (
                <tr key={c.id} className={`${idx % 2 ? "bg-slate-50" : "bg-white"} ${c.absent ? "opacity-50" : ""}`}>
                  <td className="px-2 py-1 sm:sticky sm:left-0 bg-inherit text-center font-bold">{c.numero_table}</td>
                  <td className="px-2 py-1 sm:sticky sm:left-10 bg-inherit max-w-[100px] sm:max-w-none">
                    <div className="text-xs text-[#5C736A] whitespace-nowrap truncate">{c.prenom}</div>
                    <div className="font-semibold whitespace-nowrap truncate">{c.nom}</div>
                  </td>
                  {columns.map((col, i) => {
                    const k = `${c.id}|${col.subject.id}|${col.sub || ""}`;
                    return (
                      <td key={i} className="px-1 py-1">
                        <Input
                          disabled={!canEdit || c.absent}
                          value={draft[k] ?? ""}
                          onChange={(e) => setVal(k, e.target.value)}
                          className={`h-9 w-16 text-center text-sm mx-auto ${dirty[k] ? "border-[#1F7A63] bg-[#E8F2EF] font-semibold" : ""}`}
                          inputMode="decimal"
                          placeholder={c.absent ? "—" : "0"}
                          data-testid={`note-${c.numero_table}-${col.label}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sticky bottom save bar on mobile */}
      {canEdit && (
        <div className="sticky bottom-2 z-30 sm:hidden">
          <Button
            onClick={saveDomain}
            disabled={dirtyCount === 0 || saving}
            className="w-full bg-[#1F7A63] hover:bg-[#165c4b] text-white h-12 shadow-lg"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Enregistrement…" : `Enregistrer ${dirtyCount > 0 ? `(${dirtyCount})` : ""}`}
          </Button>
        </div>
      )}
    </div>
  );
}

/* -------------------- RÉSULTATS -------------------- */
function ResultsTab({ sessionId }) {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [filterDec, setFilterDec] = useState("all");
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/evaluations/results", { params: { session_id: sessionId } });
      // Sort rows alphabetically
      data.rows = [...data.rows].sort((a, b) => sortAlpha(a.candidate, b.candidate));
      setData(data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sessionId]);

  if (!data) return <div className="skel h-32" />;
  const { stats, rows, subjects, total_bareme } = data;
  const pct = (n) => stats.total ? `${Math.round((n / stats.total) * 100)}%` : "0%";

  const filtered = rows.filter((r) => {
    if (filterDec !== "all" && r.decision !== filterDec) return false;
    const s = search.trim().toLowerCase();
    if (s && !`${r.candidate.prenom} ${r.candidate.nom}`.toLowerCase().includes(s)) return false;
    return true;
  });

  const pdfUrl = `${API_BASE}/evaluations/pdf?session_id=${sessionId}`;
  const printPdf = () => {
    const w = window.open(pdfUrl, "_blank");
    if (w) w.onload = () => setTimeout(() => w.print(), 600);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <div className="ek-card p-3 sm:p-4 text-center">
          <div className="flex items-center justify-center gap-1 text-xs text-[#5C736A]"><UsersIcon className="w-3 h-3" /> Total</div>
          <div className="text-2xl sm:text-3xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>{stats.total}</div>
        </div>
        <div className="ek-card p-3 sm:p-4 text-center border-2 border-[#1F7A63]/30 bg-[#E8F2EF]/40">
          <div className="flex items-center justify-center gap-1 text-xs text-[#5C736A]"><CheckCircle2 className="w-3 h-3 text-[#1F7A63]" /> Admis</div>
          <div className="text-2xl sm:text-3xl font-bold mt-1 text-[#1F7A63]" style={{ fontFamily: "Outfit" }}>{stats.admis}</div>
          <div className="text-[10px] sm:text-xs text-[#5C736A]">{pct(stats.admis)}</div>
        </div>
        <div className="ek-card p-3 sm:p-4 text-center border-2 border-red-200 bg-red-50/40">
          <div className="flex items-center justify-center gap-1 text-xs text-[#5C736A]"><XCircle className="w-3 h-3 text-red-600" /> Échec</div>
          <div className="text-2xl sm:text-3xl font-bold mt-1 text-red-600" style={{ fontFamily: "Outfit" }}>{stats.echec}</div>
          <div className="text-[10px] sm:text-xs text-[#5C736A]">{pct(stats.echec)}</div>
        </div>
        <div className="ek-card p-3 sm:p-4 text-center">
          <div className="flex items-center justify-center gap-1 text-xs text-[#5C736A]"><UserMinus className="w-3 h-3" /> Absents</div>
          <div className="text-2xl sm:text-3xl font-bold mt-1 text-[#5C736A]" style={{ fontFamily: "Outfit" }}>{stats.absent || 0}</div>
        </div>
      </div>

      <div className="ek-card p-3 space-y-2 sm:space-y-0 sm:flex sm:items-center sm:gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C736A]" />
          <Input className="pl-9" placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={filterDec} onValueChange={setFilterDec}>
          <SelectTrigger className="sm:w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            <SelectItem value="Admis">Admis</SelectItem>
            <SelectItem value="Échec">Échec</SelectItem>
            <SelectItem value="Absent">Absent</SelectItem>
          </SelectContent>
        </Select>
        <div className="grid grid-cols-3 sm:flex gap-2">
          <Button variant="outline" onClick={() => setPreviewOpen(true)} data-testid="res-visualiser">
            <Eye className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Visualiser</span>
          </Button>
          <Button variant="outline" onClick={printPdf} data-testid="res-imprimer">
            <Printer className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Imprimer</span>
          </Button>
          <a href={pdfUrl} target="_blank" rel="noreferrer" download>
            <Button variant="outline" className="w-full" data-testid="res-pdf"><FileText className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">PDF</span></Button>
          </a>
        </div>
        <a href={`${API_BASE}/evaluations/excel?session_id=${sessionId}`} target="_blank" rel="noreferrer">
          <Button variant="outline" className="w-full sm:w-auto" data-testid="res-excel"><Download className="w-4 h-4 sm:mr-1" /> Excel</Button>
        </a>
      </div>

      <ResultsTable rows={filtered} subjects={subjects} totalBareme={total_bareme} />

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 py-4 border-b shrink-0">
            <DialogTitle className="flex items-center justify-between">
              <span>Visualisation des résultats</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={printPdf}><Printer className="w-4 h-4 mr-1" /> Imprimer</Button>
                <a href={pdfUrl} target="_blank" rel="noreferrer">
                  <Button size="sm" className="bg-[#1F7A63] hover:bg-[#165c4b] text-white"><Download className="w-4 h-4 mr-1" /> Télécharger</Button>
                </a>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto flex-1 p-6 bg-slate-50">
            <PreviewDoc data={data} rows={filtered} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResultsTable({ rows, subjects, totalBareme }) {
  return (
    <div className="ek-card p-0 overflow-hidden">
      <div className="ek-table-wrap">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-[#E6B800]">
              <th rowSpan={2} className="px-2 py-2 text-center sm:sticky sm:left-0 bg-[#E6B800] z-10 w-10">N°</th>
              <th rowSpan={2} className="px-2 py-2 text-left sm:sticky sm:left-10 bg-[#E6B800] z-10 min-w-[90px] sm:min-w-[140px]">ÉLÈVE</th>
              {subjects.map((s) => (
                <th key={s.id}
                    colSpan={s.has_sub_domains ? (s.sub_domains || []).length + 1 : 1}
                    className="px-2 py-2 text-center font-bold border-l border-[#b38f00]/40">
                  {s.nom}
                </th>
              ))}
              <th rowSpan={2} className="px-2 py-2 text-center border-l border-[#b38f00]/40">Total /{totalBareme}</th>
              <th rowSpan={2} className="px-2 py-2 text-center">Rang</th>
              <th rowSpan={2} className="px-2 py-2 text-center">Décision</th>
            </tr>
            <tr className="bg-[#E6B800]">
              {subjects.flatMap((s) => {
                if (s.has_sub_domains) {
                  const cols = (s.sub_domains || []).map((sd) => (
                    <th key={`sd-${s.id}-${sd.nom}`} className="px-1 py-1 text-center text-[10px]">{sd.nom}<div className="text-[9px] font-normal">/{sd.points}</div></th>
                  ));
                  cols.push(<th key={`tot-${s.id}`} className="px-1 py-1 text-center text-[10px]">TOT<div className="text-[9px] font-normal">/{s.points}</div></th>);
                  return cols;
                }
                return [<th key={`p-${s.id}`} className="px-1 py-1 text-center text-[10px]">/{s.points}</th>];
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={r.candidate.id} className={`${idx % 2 ? "bg-slate-50" : "bg-white"} ${r.decision === "Absent" ? "opacity-60" : ""}`}>
                <td className="px-2 py-2 text-center font-bold sm:sticky sm:left-0 bg-inherit">{r.candidate.numero_table}</td>
                <td className="px-2 py-2 font-semibold sm:sticky sm:left-10 bg-inherit whitespace-nowrap max-w-[100px] sm:max-w-none truncate sm:text-clip">
                  <span className="hidden sm:inline"><span className="font-normal">{r.candidate.prenom}</span> {r.candidate.nom}</span>
                  <span className="sm:hidden">{r.candidate.nom.slice(0, 8)}{r.candidate.nom.length > 8 ? "…" : ""}</span>
                </td>
                {subjects.flatMap((s) => {
                  const p = r.subjects[s.id] || {};
                  if (s.has_sub_domains) {
                    const cells = (s.sub_domains || []).map((sd) => (
                      <td key={`sd-${s.id}-${sd.nom}`} className="px-1 py-2 text-center">{(p.sub_domains || {})[sd.nom] ?? "—"}</td>
                    ));
                    cells.push(<td key={`tot-${s.id}`} className="px-1 py-2 text-center font-semibold border-l border-slate-200">{p.total != null ? p.total : "—"}</td>);
                    return cells;
                  }
                  return [<td key={`p-${s.id}`} className="px-1 py-2 text-center">{p.total ?? "—"}</td>];
                })}
                <td className="px-2 py-2 text-center font-bold text-[#1F7A63] border-l border-slate-200">{r.total != null ? r.total.toFixed(1) : "—"}</td>
                <td className="px-2 py-2 text-center">{r.rang || "—"}</td>
                <td className="px-2 py-2 text-center">
                  {r.decision === "Admis" && <span className="bg-[#E8F2EF] text-[#1F7A63] px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap">Admis</span>}
                  {r.decision === "Échec" && <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded-full text-xs font-semibold">Échec</span>}
                  {r.decision === "Absent" && <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full text-xs font-semibold">Absent</span>}
                  {r.decision === "—" && <span className="text-[#5C736A] text-xs">—</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6 + subjects.length * 2} className="text-center py-10 text-[#5C736A] text-sm">Aucun résultat.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PreviewDoc({ data, rows }) {
  const s = data.session;
  return (
    <div className="bg-white shadow-md mx-auto" style={{ width: "100%", maxWidth: "297mm", fontFamily: "Times New Roman, serif" }}>
      <div className="p-8">
        <div className="text-center text-xs">RÉPUBLIQUE DU SÉNÉGAL — Un Peuple - Un But - Une Foi</div>
        <div className="text-center text-xs mt-1">IA : {s.ia || "—"} | IEF : {s.ief || "—"} | Année : {s.annee_scolaire || "—"}</div>
        <h2 className="text-center text-xl font-bold text-[#1F7A63] mt-2">{s.centre || "—"}</h2>
        <h3 className="text-center text-lg font-bold mt-1">{s.nom}</h3>
        <div className="text-center text-xs text-[#5C736A] mt-1">
          Total barème : <strong>{data.total_bareme}</strong> · Admission ≥ {data.admission_min} · Repêchage ≥ {data.repechage_min}
        </div>
        <ResultsTable rows={rows} subjects={data.subjects} totalBareme={data.total_bareme} />
        <div className="grid grid-cols-2 gap-4 mt-12 text-center text-sm">
          <div>
            <div className="font-bold">LE MAÎTRE</div>
            <div className="italic mt-8">_____________________</div>
          </div>
          <div>
            <div className="font-bold">LE DIRECTEUR</div>
            <div className="italic mt-8">_____________________</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------- BULLETINS -------------------- */
function BulletinsTab({ sessionId }) {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState(1);
  const load = async () => {
    try {
      const { data } = await api.get("/evaluations/results", { params: { session_id: sessionId } });
      data.rows = [...data.rows].sort((a, b) => sortAlpha(a.candidate, b.candidate));
      setData(data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sessionId]);

  if (!data) return <div className="skel h-32" />;
  const filtered = data.rows.filter((r) => {
    const s = search.trim().toLowerCase();
    return !s || `${r.candidate.prenom} ${r.candidate.nom}`.toLowerCase().includes(s);
  });

  const MODES = [
    { id: 1, label: "1 par page (portrait)", desc: "Un bulletin par feuille" },
    { id: 2, label: "2 par page (paysage)", desc: "Deux bulletins côte à côte" },
    { id: 3, label: "Toute la classe", desc: "Tous les bulletins à la suite" },
  ];

  const url = (cid) => {
    const params = new URLSearchParams({ session_id: sessionId, mode: String(mode) });
    if (cid) params.set("candidate_id", cid);
    return `${API_BASE}/evaluations/bulletins/pdf?${params.toString()}`;
  };

  const downloadAll = () => {
    const a = document.createElement("a");
    a.href = url();
    a.download = `bulletins_mode${mode}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
  };
  const printAll = () => {
    const w = window.open(url(), "_blank");
    if (w) w.onload = () => setTimeout(() => w.print(), 600);
  };

  return (
    <div className="space-y-4">
      {/* Mode selector */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {MODES.map((m) => (
          <button
            key={m.id}
            data-testid={`bull-mode-${m.id}`}
            onClick={() => setMode(m.id)}
            className={`text-left ek-card p-4 border-2 transition-all ${
              mode === m.id ? "border-[#1F7A63] bg-[#E8F2EF]" : "border-transparent"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="w-9 h-9 rounded-lg bg-[#1F7A63] grid place-items-center text-white font-bold">{m.id}</div>
              {mode === m.id && <span className="text-xs bg-[#E6B800] text-[#1A2E28] px-2 py-0.5 rounded-full font-semibold">Choisi</span>}
            </div>
            <div className="mt-2 font-semibold text-sm">{m.label}</div>
            <div className="text-xs text-[#5C736A] mt-0.5">{m.desc}</div>
          </button>
        ))}
      </div>

      {/* Actions bar */}
      <div className="ek-card p-3 flex flex-wrap items-center gap-2">
        <div className="text-sm text-[#5C736A] mr-auto">
          Mode <b className="text-[#1A2E28]">{mode}</b> — <b className="text-[#1A2E28]">{filtered.length}</b> bulletin(s)
        </div>
        <a href={url()} target="_blank" rel="noreferrer">
          <Button variant="outline" data-testid="bull-view-all"><FileText className="w-4 h-4 mr-1" /> Visualiser</Button>
        </a>
        <Button variant="outline" onClick={printAll} data-testid="bull-print-all">
          <FileText className="w-4 h-4 mr-1" /> Imprimer
        </Button>
        <Button onClick={downloadAll} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="bull-all">
          <FileText className="w-4 h-4 mr-1" /> Télécharger tous
        </Button>
      </div>

      {/* Search + list */}
      <div className="ek-card p-3">
        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C736A]" />
          <Input className="pl-9" placeholder="Rechercher un élève…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="border-t border-[#E2E8F0] -mx-3">
          {filtered.map((r) => (
            <div key={r.candidate.id} className="px-3 py-3 flex items-center justify-between gap-3 flex-wrap border-b border-[#E2E8F0]" data-testid={`bull-row-${r.candidate.numero_table}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-[#1F7A63] text-white grid place-items-center font-bold shrink-0">
                  {r.candidate.numero_table}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold truncate"><span className="font-normal">{r.candidate.prenom}</span> {r.candidate.nom}</div>
                  <div className="text-xs text-[#5C736A]">
                    Total : {r.total != null ? `${r.total.toFixed(1)} / ${data.total_bareme}` : "—"} · {r.decision}
                  </div>
                </div>
              </div>
              <a href={url(r.candidate.id)} target="_blank" rel="noreferrer">
                <Button size="sm" className="bg-[#E6B800] hover:bg-[#b38f00] text-[#1A2E28]">
                  <ChevronRight className="w-4 h-4 mr-1" /> Bulletin
                </Button>
              </a>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-3 py-8 text-center text-sm text-[#5C736A]">Aucun élève.</div>
          )}
        </div>
      </div>
    </div>
  );
}
